import { Link } from "react-router-dom";
import { ROUTES } from "@routing/routes";
import type { CreateClientResponse } from "@models/organization";
import { formatDateTime } from "@utils/formatter";

interface CreateClientOutcomeProps {
    // The API's 201; undefined when it carried no JSON answer.
    response: CreateClientResponse | undefined;
}

// What an accepted New Client create did (C3, API PR 419). The client exists
// whatever followed, so the drawer says so first. Its first location's
// approval and the invitation email can each fail after that (PR 419 round 1),
// and each failure is said as one, with the way out.
export const CreateClientOutcome = ({ response }: CreateClientOutcomeProps) => {
    if (!response) {
        return (
            <div className="flex flex-col gap-3">
                <p role="status" className="font-medium">
                    Client created.
                </p>
                <p>
                    Find it in the Clients list to check its location and
                    invitation.
                </p>
            </div>
        );
    }

    // Read defensively (PR 440 S1): an answer without its location must not
    // crash the outcome, nor read as an approval.
    const { location } = response;

    return (
        <div className="flex flex-col gap-3">
            <p role="status" className="font-medium">
                Client created.
            </p>
            {location?.approved === true ? (
                <p>
                    The first location is approved. It waits for payment until
                    the customer adds a card after signing up.
                </p>
            ) : (
                <p role="alert" className="text-error">
                    {location?.error?.trim() ||
                        "The first location was not approved. Approve it from the client page."}
                </p>
            )}
            {response.invitationSent ? (
                <p>
                    An invitation was sent to {response.invitationEmail}. Its
                    link works until{" "}
                    {formatDateTime(response.invitationExpiresAt)}.
                </p>
            ) : (
                <p role="alert" className="text-error">
                    The invitation email to {response.invitationEmail}{" "}
                    didn&apos;t send. Use Resend invitation on the client page
                    to send a new link.
                </p>
            )}
            <Link
                to={ROUTES.ADMIN_CLIENTS_DETAIL(response.organizationId)}
                className="link font-semibold"
            >
                Open client
            </Link>
        </div>
    );
};
